import { useEffect, useState } from 'react';
import type { SeriesStatus } from './seriesStatus';

const guidance: Record<string, string> = {
  preparing: 'Preparing the lobby and sending invitations.',
  teams_fill: 'Waiting for the selected starters to take their playing slots.',
  ready_check: 'Check both lineups and team readiness before approving play.',
  decision: 'Teams are choosing side and selection priority in Dota.',
  launching: 'The game is launching.',
  running: 'Match in progress. The bot is watching for the result.',
  outcome: 'Waiting for a confirmed match result.',
  result_review:
    'The result needs a referee. Confirm the winner or order a remake.',
  between_games: 'Between games. The next lobby keeps the series score.',
  recovering:
    'Automation is held after a restart. Reconcile the recorded lobby to continue.',
  failed: 'Automation stopped. Review the error before retrying or cancelling.',
};

export function SeriesProgress({ series: s }: { series: SeriesStatus }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!s.deadline) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [s.deadline]);
  const seconds = s.deadline
    ? Math.max(0, Math.ceil((Date.parse(s.deadline) - now) / 1000))
    : 0;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <span className="text-sm">
          {s.team_a_name || 'Team A'}{' '}
          <strong className="mx-2 text-xl tabular-nums text-ink">
            {s.radiant_wins} : {s.dire_wins}
          </strong>{' '}
          {s.team_b_name || 'Team B'}
        </span>
        <span className="text-xs text-ink-dim">
          {s.format.toUpperCase()} · Game {s.game_number}
          {s.attempt > 1 ? ` · Attempt ${s.attempt}` : ''}
        </span>
        {s.deadline && (
          <time
            dateTime={s.deadline}
            title={new Date(s.deadline).toLocaleString()}
            className={`text-xs tabular-nums ${seconds < 60 ? 'text-warn' : 'text-ink-dim'}`}
          >
            {seconds
              ? `${Math.floor(seconds / 60)}m ${seconds % 60}s remaining`
              : 'Deadline reached'}
          </time>
        )}
      </div>
      {guidance[s.phase] && (
        <p className="text-sm text-ink-dim">
          {s.held
            ? 'Automation is held. Resume when the issue is resolved.'
            : guidance[s.phase]}
        </p>
      )}
      {s.phase === 'ready_check' && (
        <div className="flex flex-wrap gap-4 text-xs">
          {(['radiant', 'dire'] as const).map((side) => (
            <span
              key={side}
              className={
                s.ready_teams?.includes(side) ? 'text-ok' : 'text-ink-dim'
              }
            >
              {side === 'radiant'
                ? s.team_a_name || 'Team A'
                : s.team_b_name || 'Team B'}
              : {s.ready_teams?.includes(side) ? 'Ready' : 'Awaiting ready'}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
